/* eslint-disable @stencil-community/required-jsdoc */
import { isEmpty } from '@springbokagency/stencil-base';
import { Component, Host, h, Prop, Event, EventEmitter } from '@stencil/core';

import type { IconName, IconNameType } from '../icon/icon.types';
import type { ColorVariant } from '../../../types/colors.types';

@Component({
  tag: 'efb-button',
  styleUrl: 'button.scss',
  shadow: true,
})
export class EfbButton {
  /**
   * Props
   */

  @Prop() text: string;

  @Prop() href?: string;

  @Prop() target?: '_blank' | 'internal';

  @Prop() type?: 'button' | 'submit' = 'button';

  @Prop() action?:
    | 'open-menu'
    | 'close-menu'
    | 'open-video-modal'
    | 'close-video-modal'
    | 'load-more-faq';

  // ::TODO:: make videoModalGroup props required if action's value = 'open-video-modal'
  @Prop() videoModalGroup?: string;

  @Prop() variant?: ColorVariant;

  @Prop() whiteBg?: boolean;

  @Prop() ghost?: boolean;

  @Prop() compact?: boolean;

  @Prop() compactPlus?: boolean;

  @Prop() small?: boolean;

  @Prop() icon?: IconName | IconNameType;

  @Prop() hasLeftIcon?: boolean;

  @Prop() fullWidth?: boolean;

  @Prop() fullWidthMobile?: boolean;

  @Prop() focusable?: 0 | -1 = 0;

  @Prop() isLoading?: boolean;

  @Prop() dataAnalytics?: string;

  /**
   * Events
   */

  @Event() efbButtonClick: EventEmitter<{
    type?: string;
    href?: string;
    target?: string;
    action?: string;
    videoModalGroup?: string;
    value?: any;
    dataAnalytics: string;
  }>;

  private handleClick() {
    const { type, href, target, action, videoModalGroup, dataAnalytics } = this;

    this.efbButtonClick.emit({ type, href, target, action, videoModalGroup, dataAnalytics });
  }

  /**
   * Render
   */

  render() {
    const TagType = isEmpty(this.href) ? 'button' : 'a';

    const attributes =
      TagType === 'button'
        ? { type: this.type }
        : {
            href: this.href,
            target: this.target,
            rel: this.target === '_blank' ? 'noreferrer' : '',
          };

    if (this.compact) attributes['aria-label'] = this.text;

    if (!isEmpty(this.dataAnalytics)) attributes['data-analytics'] = this.dataAnalytics;

    return (
      <Host class={this.fullWidthMobile ? 'is-full-width-mobile' : ''}>
        <TagType
          class={`button
          ${this.ghost ? 'is-ghost' : ''}
          ${this.compact ? 'is-compact' : ''}
          ${this.compactPlus ? 'is-compact-plus' : ''}
          ${this.small ? 'is-small' : ''}
          ${!isEmpty(this.variant) ? `is-variant-${this.variant}` : ''}
          ${this.whiteBg ? 'has-white-bg' : ''}
          ${this.hasLeftIcon ? 'has-left-icon' : ''}
          ${this.fullWidth ? 'is-full-width' : ''}
          ${this.isLoading ? 'is-loading' : ''}`}
          {...attributes}
          tabIndex={this.focusable}
          onClick={this.handleClick.bind(this)}
        >
          {/* text */}
          {!this.compact && !this.compactPlus && <span class="text">{this.text}</span>}

          {/* icon */}
          {this.icon && (
            <span class="icon">
              <efb-icon name={this.icon} />
            </span>
          )}
        </TagType>
      </Host>
    );
  }
}
